import { useEffect } from "react";
import { CheckCircle2, Sparkles, Eye, Globe, Loader2, PencilLine, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";
import { isReadyToPublish, type OrgProfileStrength } from "@/lib/orgProfileStrength";

interface CompletionProps {
  orgName: string;
  /** Score the final save produced; null when it could not be read. */
  strength: OrgProfileStrength | null;
  isPublished: boolean;
  isPublishing: boolean;
  onPreview: () => void;
  onPublish: () => void;
  /** Close the wizard and open the profile editor to keep filling it in. */
  onFinishInEditor: () => void;
  onDone: () => void;
}

const OrgOnboardingCompletion = ({
  orgName,
  strength,
  isPublished,
  isPublishing,
  onPreview,
  onPublish,
  onFinishInEditor,
  onDone,
}: CompletionProps) => {
  // Only a profile that clears the publish threshold is "ready". A failed strength
  // read (null) is treated as not-ready: never claim ready or push Publish on a
  // number we could not confirm.
  const ready = strength ? isReadyToPublish(strength) : false;
  const pct = strength?.pct ?? 0;
  const topMissing = strength?.missing.slice(0, 3) ?? [];

  useEffect(() => {
    if (!ready) return;
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

    // Brand palette: teal primary, teal soft, wordmark gold (docs/design-system.md).
    const colors = ["#13565A", "#2E7A7E", "#FFB703"];

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors,
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors,
      });
    }, 250);

    return () => clearInterval(interval);
  }, [ready]);

  // Below the publish threshold: honest progress, no confetti, no Publish. Steer
  // the admin back into the editor rather than pushing an empty profile live.
  if (!ready) {
    const circumference = 2 * Math.PI * 42;
    // Plain-speak stage from how far along the profile is — the ring still shows
    // momentum, but no percentage is ever put on screen.
    const stage =
      pct < 25
        ? {
            headline: "Let's keep building",
            subline: "Add a few key details so agents get who you are.",
          }
        : pct < 50
        ? {
            headline: "It's coming together",
            subline: "A bit more and your story will really land.",
          }
        : {
            headline: "You're almost there",
            subline: "Just a little more before you publish.",
          };
    return (
      <div className="text-center space-y-7 py-6">
        <div className="relative inline-flex items-center justify-center">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
            <circle cx="48" cy="48" r="42" fill="none" strokeWidth="6" className="text-muted" stroke="currentColor" />
            <circle
              cx="48"
              cy="48"
              r="42"
              fill="none"
              strokeWidth="6"
              strokeLinecap="round"
              className="text-primary"
              stroke="currentColor"
              strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="h-8 w-8 text-primary" />
          </span>
        </div>

        <div className="space-y-3">
          <h2 className="font-heading text-h3 text-ink-900">{stage.headline}</h2>
          <p className="text-muted-foreground max-w-sm mx-auto">{stage.subline}</p>
        </div>

        {topMissing.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2 max-w-lg mx-auto">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              Still missing
            </span>
            {topMissing.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1.5 text-xs bg-warning/10 text-warning px-2.5 py-1 rounded-full"
              >
                {item}
              </span>
            ))}
          </div>
        )}

        <div className="space-y-3 max-w-sm mx-auto">
          <Button size="lg" className="w-full gap-2" onClick={onFinishInEditor}>
            <PencilLine className="h-5 w-5" />
            Finish in the editor
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" className="w-full gap-2" onClick={onPreview}>
            <Eye className="h-5 w-5" />
            Preview public story
          </Button>
          <button
            type="button"
            onClick={onDone}
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
          >
            I'll do this later
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-7 py-6">
      <div className="relative inline-flex">
        <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center animate-scale-in">
          <CheckCircle2 className="h-12 w-12 text-white" />
        </div>
        <div className="absolute -top-2 -right-2">
          <Sparkles className="h-8 w-8 text-gold-accent animate-pulse" />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="font-heading text-h2 text-ink-900">Your story is ready!</h2>
        <p className="text-muted-foreground max-w-sm mx-auto">
          {isPublished
            ? `${orgName} is live and visible to agents on Compaire.`
            : `${orgName}'s story is set up. Publish it whenever you're ready to go live.`}
        </p>
      </div>

      <div className="space-y-3 max-w-sm mx-auto">
        <Button size="lg" variant="outline" className="w-full gap-2" onClick={onPreview}>
          <Eye className="h-5 w-5" />
          Preview public story
        </Button>

        {!isPublished ? (
          <Button size="lg" className="w-full gap-2" onClick={onPublish} disabled={isPublishing}>
            {isPublishing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Globe className="h-5 w-5" />}
            {isPublishing ? "Publishing…" : "Publish now"}
          </Button>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm font-medium text-success">
            <Globe className="h-4 w-4" />
            Published
          </div>
        )}

        <button
          type="button"
          onClick={onDone}
          className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
        >
          {isPublished ? "Done" : "I'll publish later"}
        </button>
      </div>
    </div>
  );
};

export default OrgOnboardingCompletion;
